// Nouveau mot de passe : page ouverte par le lien de l'e-mail « mot de passe oublié » (app ou site).
// Supabase ouvre une session de récupération depuis le lien ; on y change le mot de passe, puis on
// referme la session (on se reconnecte ensuite dans l'app avec le nouveau mot de passe).

import { t } from '@shared/i18n';
import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { configError, supabase } from '../supabase';

const MIN_LENGTH = 8;

export default function ResetPassword() {
  const [phase, setPhase] = useState<'checking' | 'ready' | 'done' | 'invalid'>('checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supabase) {
      setPhase('invalid');
      return;
    }
    // lien expiré ou déjà utilisé : Supabase renvoie l'erreur dans l'adresse
    if (/error=|error_code=/.test(window.location.hash + window.location.search)) {
      setPhase('invalid');
      return;
    }
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || session) setPhase((p) => (p === 'checking' ? 'ready' : p));
    });
    supabase.auth.getSession().then(({ data: d }) => {
      if (d.session) setPhase((p) => (p === 'checking' ? 'ready' : p));
    });
    // pas de session après quelques secondes : lien invalide
    const timer = setTimeout(() => setPhase((p) => (p === 'checking' ? 'invalid' : p)), 4000);
    return () => {
      data.subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_LENGTH)
      return setError(t(`Au moins ${MIN_LENGTH} caractères.`, `At least ${MIN_LENGTH} characters.`));
    if (password !== confirm)
      return setError(t('Les deux mots de passe sont différents.', 'The passwords do not match.'));
    setBusy(true);
    try {
      const { error: err } = await supabase!.auth.updateUser({ password });
      if (err) throw err;
      await supabase!.auth.signOut();
      setPhase('done');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container" style={{ maxWidth: 440, paddingTop: 56 }}>
      <div className="card stack">
        <div style={{ textAlign: 'center' }}>
          <span className="logo big" style={{ width: 64, height: 64, fontSize: '1.6rem', borderRadius: 18 }}>
            FM
          </span>
          <h2 style={{ marginTop: 16 }}>{t('Nouveau mot de passe', 'New password')}</h2>
        </div>

        {phase === 'checking' && <p className="muted">{t('Vérification du lien…', 'Checking the link…')}</p>}

        {phase === 'invalid' && (
          <>
            <div className="notice">
              {supabase
                ? t(
                    'Ce lien n’est plus valable (expiré ou déjà utilisé). Dans l’app FollowMe, touche « Mot de passe oublié ? » pour en recevoir un nouveau.',
                    'This link is no longer valid (expired or already used). In the FollowMe app, tap “Forgot your password?” to get a new one.',
                  )
                : configError()}
            </div>
            <Link to="/">{t('← Accueil', '← Home')}</Link>
          </>
        )}

        {phase === 'ready' && (
          <form className="stack" onSubmit={submit}>
            <p className="muted small" style={{ margin: 0 }}>
              {t('Choisis ton nouveau mot de passe FollowMe.', 'Choose your new FollowMe password.')}
            </p>
            <label className="field">
              {t('Nouveau mot de passe', 'New password')}
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
            <label className="field">
              {t('Confirmer le mot de passe', 'Confirm the password')}
              <input
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </label>
            {error && <div className="notice">{error}</div>}
            <button className="btn" disabled={busy}>
              {busy ? t('Enregistrement…', 'Saving…') : t('Enregistrer le mot de passe', 'Save the password')}
            </button>
          </form>
        )}

        {phase === 'done' && (
          <div className="notice ok">
            <strong>{t('✓ Mot de passe modifié.', '✓ Password changed.')}</strong>
            <div>
              {t(
                'Retourne dans l’app FollowMe et connecte-toi avec ton nouveau mot de passe.',
                'Go back to the FollowMe app and sign in with your new password.',
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
